import { Check, Copy } from "lucide-react";
import { Fragment, useState, type ReactNode } from "react";
import { copyToClipboard } from "@/lib/utils";
import { codeLinks, parseCodeRef } from "@/lib/code-links";

/**
 * Markdown mínimo e SEGURO para respostas da IA: nunca interpreta HTML; links só
 * http(s). Suficiente para parágrafos, títulos, listas, código e ênfase.
 */
export function Markdown({ text }: { text: string }) {
  return <div className="space-y-2.5 text-[13px] leading-relaxed">{parseBlocks(text)}</div>;
}

function CodeBlock({ lang, code }: { lang: string; code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="overflow-hidden rounded-md border border-border">
      <div className="flex items-center justify-between border-b border-border bg-bg-muted px-2.5 py-1">
        <span className="text-[11px] uppercase tracking-[0.06em] text-fg-subtle font-medium">
          {lang || "código"}
        </span>
        <button
          className="flex items-center gap-1 text-[11px] text-fg-muted hover:text-fg"
          onClick={async () => {
            setCopied(await copyToClipboard(code));
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}{" "}
          {copied ? "Copiado" : "Copiar"}
        </button>
      </div>
      <pre className="overflow-x-auto bg-bg-subtle p-2.5 font-mono text-[12px] leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function parseBlocks(src: string): ReactNode[] {
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const out: ReactNode[] = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    const fence = /^```\s*([\w+#.-]*)\s*$/.exec(line);
    if (fence) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i]!)) code.push(lines[i++]!);
      i++; // fecha a cerca (ou fim do texto em streaming)
      out.push(<CodeBlock key={key++} lang={fence[1] ?? ""} code={code.join("\n")} />);
      continue;
    }
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1]!.length;
      out.push(
        <p key={key++} className={level <= 2 ? "pt-1 text-[14px] font-semibold" : "font-semibold"}>
          {inline(heading[2]!)}
        </p>,
      );
      i++;
      continue;
    }
    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i]!))
        items.push(lines[i++]!.replace(/^\s*([-*]|\d+\.)\s+/, ""));
      const List = ordered ? "ol" : "ul";
      out.push(
        <List
          key={key++}
          className={
            ordered ? "list-decimal space-y-1 pl-5" : "list-disc space-y-1 pl-5 marker:text-fg-subtle"
          }
        >
          {items.map((it, j) => (
            <li key={j}>{inline(it)}</li>
          ))}
        </List>,
      );
      continue;
    }
    if (!line.trim()) {
      i++;
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !/^```|^#{1,4}\s|^\s*([-*]|\d+\.)\s+/.test(lines[i]!))
      para.push(lines[i++]!);
    out.push(<p key={key++}>{inline(para.join(" "))}</p>);
  }
  return out;
}

const INLINE = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*\s][^*]*\*|\[[^\]]+\]\((https?:\/\/[^\s)]+)\))/g;

function inline(text: string): ReactNode {
  const parts: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  INLINE.lastIndex = 0;
  while ((m = INLINE.exec(text))) {
    if (m.index > last) parts.push(<Fragment key={k++}>{text.slice(last, m.index)}</Fragment>);
    const tok = m[0];
    const ref = tok.startsWith("`") ? parseCodeRef(tok.slice(1, -1)) : null;
    if (ref && codeLinks.current?.exists(ref.path)) {
      const target = ref;
      parts.push(
        <button
          key={k++}
          type="button"
          onClick={() => codeLinks.current?.open(target.path, target.line)}
          className="rounded-sm border border-border bg-bg-muted px-1 py-0.5 font-mono text-[12px] underline-offset-2 hover:border-border-strong hover:underline"
          title="Abrir no editor"
        >
          {tok.slice(1, -1)}
        </button>,
      );
    } else if (tok.startsWith("`"))
      parts.push(
        <code key={k++} className="rounded-sm bg-bg-muted px-1 py-0.5 font-mono text-[12px]">
          {tok.slice(1, -1)}
        </code>,
      );
    else if (tok.startsWith("**")) parts.push(<strong key={k++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("[")) {
      const label = /^\[([^\]]+)\]/.exec(tok)![1];
      parts.push(
        <a
          key={k++}
          href={m[2]}
          target="_blank"
          rel="noreferrer noopener"
          className="underline underline-offset-2"
        >
          {label}
        </a>,
      );
    } else parts.push(<em key={k++}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(<Fragment key={k}>{text.slice(last)}</Fragment>);
  return parts;
}
